import type { MessageTiming, ThreadMessageLike } from '@assistant-ui/react';
import {
  SPEC_PRESENT_RESULT_TYPE,
  SPEC_SOURCES_PART_TYPE,
  type SpecCitationHandle,
  type SpecFilePart,
  type SpecRunErrorCode,
  type SpecSource,
  isSpecRunErrorCode,
  readSpecCites,
  readSpecFilePart,
  readSpecPresent,
  readSpecSources,
} from '@parcel/types';
import {
  type SkillAuthoringSessionSummary,
  SkillAuthoringSessionSummarySchema,
  type SkillLocator,
  type SkillWorkflowInput,
  type SpecBootstrap,
  type SpecLaunchState,
  type SpecMemberSeatState,
  type SpecRunStatus,
  type SpecUsageSummary,
} from '@parcel/types';
import {
  type SpecToolResultProjection,
  asToolRecords,
  asToolStep,
  readToolResultProjection,
} from '@parcel/ui';
import { ApiError, apiErrorCode, apiFetch, apiFetchBlob } from './api.js';
import {
  SPEC_WORKER_BASE_URL,
  isSpecWorkerConfigured,
  readSpecWorkerBaseUrl,
} from './spec-config.js';
import { type SpecFileRecord, readSpecFileRecord } from './spec-files.js';
import { readSpecQuestionSet } from './spec-questions.js';
import { supabase } from './supabase.js';

/**
 * Typed browser client for Spec, plus the pure state machine that turns the
 * Worker's SSE frames and the API's persisted messages into Assistant UI
 * messages.
 *
 * Two backends, split by role (ledger ruling R8):
 *
 *  - The Parcel API (`VITE_API_URL`) serves bootstrap and every conversation
 *    read/write, including rename. Reached through the shared `apiFetch`, which
 *    already attaches the member's Supabase bearer token, sets the content type,
 *    prefixes `/api/v1`, and raises `ApiError`.
 *  - The Spec Worker (`VITE_SPEC_URL`) serves only send, stream, and cancel. It
 *    is a different origin, so those three calls get their own small wrapper
 *    that reuses the same Supabase session token.
 *
 * Nothing here has a service-role shortcut: every byte the browser sees came
 * back from a member-authenticated request.
 */

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

/** The one `parts` schema version this build knows how to render. */
export const SPEC_MESSAGE_PARTS_VERSION = 1;

/** Envelope version the API and the Worker both parse. */
export const SPEC_CONTEXT_ENVELOPE_VERSION = 2;

export type SpecMessageRole = 'user' | 'assistant' | 'tool';
export type SpecMessageStatus = 'pending' | 'complete' | 'failed' | 'canceled';

export interface SpecConversationSummary {
  id: string;
  workspaceId: string;
  creatorMemberId: string;
  title: string | null;
  lastMessageAt: string | null;
  /** Latest completed assistant reply; list reads use this for unread status. */
  lastAnswerAt?: string | null;
  /** Every reply up to this time has been seen; absent from an API that predates it. */
  seenAnswerAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Set by the list read only; a run still in flight, whoever is watching it. */
  runningRunId?: string | null;
}

/** A create may bind the conversation to one hidden system skill workflow. */
export interface SpecCreateConversationOptions {
  skillWorkflow?: SkillWorkflowInput;
}

/** An ordinary summary, plus the binding when (and only when) one was requested. */
export interface SpecCreatedConversation extends SpecConversationSummary {
  skillAuthoringSession?: SkillAuthoringSessionSummary;
}

export interface SpecRunSummary {
  id: string;
  conversationId: string;
  memberId: string;
  status: SpecRunStatus;
  version: number;
  policyVersion: number;
  toolRegistryVersion: number;
  errorCode: string | null;
  completedAt: string | null;
  /** When the answer was done. Before `completedAt`, which waits on the title step. */
  answeredAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SpecMessageAttachedSkill {
  locator: SkillLocator;
  /** False when the run never read it: attached, but dropped before the model. */
  loaded: boolean;
}

export type SpecFeedbackRating = 'up' | 'down';
export type SpecFeedbackReason = 'wrong_data' | 'did_not_follow' | 'too_slow' | 'other';

/** The calling member's own rating of an answer. */
export interface SpecMessageFeedback {
  rating: SpecFeedbackRating;
  reason: SpecFeedbackReason | null;
}

export interface SpecMessage {
  id: string;
  conversationId: string;
  runId: string;
  /** The browser's idempotency key for the send that produced this message. */
  clientMessageId: string | null;
  role: SpecMessageRole;
  status: SpecMessageStatus;
  /** AI SDK shaped. Opaque here; `partsVersion` decides whether it is renderable. */
  parts: readonly unknown[];
  partsVersion: number;
  contextSnapshotId: string | null;
  /** Optional: an API from before the column omits it entirely. */
  attachedSkills?: readonly SpecMessageAttachedSkill[];
  /** Optional: only the transcript read carries it, and only for the caller. */
  feedback?: SpecMessageFeedback | null;
  sequence: number;
  createdAt: string;
  updatedAt: string;
}

export interface SpecConversationDetail {
  conversation: SpecConversationSummary;
  messages: readonly SpecMessage[];
  activeRun: SpecRunSummary | null;
  latestRun: SpecRunSummary | null;
  /** True when older messages were dropped for the byte budget. */
  truncated: boolean;
  /** Present only on a conversation bound to a system skill workflow. */
  skillAuthoringSession?: SkillAuthoringSessionSummary;
}

/** Content-free invalidation from a successful authoring draft write. */
export interface SkillDraftChangedEvent {
  skillId: string;
  draftVersion: number;
  workflowKey: string;
  workflowVersion: number;
  changed: boolean;
}

/** Runtime card key. Reconstructs may omit draftVersion until get returns. */
export interface SkillDraftInvalidation {
  skillId: string;
  draftVersion: number | null;
  workflowKey: string;
  workflowVersion: number;
  changed: boolean;
}

/** The runtime event only. Lookalike assistant text must not pass this. */
export function parseSkillDraftChanged(raw: unknown): SkillDraftChangedEvent | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.skillId !== 'string' || row.skillId.length === 0) return null;
  if (typeof row.draftVersion !== 'number' || !Number.isInteger(row.draftVersion)) return null;
  if (typeof row.workflowKey !== 'string' || row.workflowKey.length === 0) return null;
  if (typeof row.workflowVersion !== 'number' || !Number.isInteger(row.workflowVersion)) {
    return null;
  }
  if (typeof row.changed !== 'boolean') return null;
  return {
    skillId: row.skillId,
    draftVersion: row.draftVersion,
    workflowKey: row.workflowKey,
    workflowVersion: row.workflowVersion,
    changed: row.changed,
  };
}

/** What a send carries: the text (file chips as directives) and one part per file. */
export type SpecOutgoingPart = { type: 'text'; text: string } | SpecFilePart;

// ---------------------------------------------------------------------------
// Context envelope
//
// NOT free design. The shape is pinned by shipped server code:
// `contextEnvelopeBody` in apps/spec/src/index.ts and `validateAndFreezeContext`
// in apps/api/src/lib/spec/context.ts, both of which STRIP unknown keys. Two
// absences are deliberate and must stay: no `orderedItems` (the server builds
// the frozen items from verified references, so a client cannot author what the
// model reads) and no `hash` (the digest is derived from those items, so a
// client cannot make two contexts share one).
// ---------------------------------------------------------------------------

export type SpecContextScopeDataset = 'account' | 'contact' | 'project';
export type SpecContextRecordType = 'account' | 'contact' | 'project' | 'view';
/** Kept for stored snapshots: signals were context before v2 and old reveals still name them. */
export type SpecContextEntityType = SpecContextRecordType | 'signal';
export type SpecContextViewDataset = SpecContextScopeDataset;

export type SpecContextFocus =
  | { kind: 'scope'; dataset: SpecContextScopeDataset }
  | { kind: 'record'; type: SpecContextRecordType; id: string };

export interface SpecContextEnvelope {
  version: 2;
  focus: SpecContextFocus | null;
}

export function emptySpecContextEnvelope(): SpecContextEnvelope {
  return { version: SPEC_CONTEXT_ENVELOPE_VERSION, focus: null };
}

/** Everything a send carries besides its text, produced at send time by the context store. */
export interface SpecSendContext {
  envelope: SpecContextEnvelope;
  /** Skill references (`custom/<id>` or `official/<id>`) attached to this message. */
  skills: readonly string[];
  /** Connection ids switched off for this conversation. */
  connectionsOff: readonly string[];
  /** The chat's Web switch is off. */
  webOff: boolean;
}

export function emptySpecSendContext(): SpecSendContext {
  return { envelope: emptySpecContextEnvelope(), skills: [], connectionsOff: [], webOff: false };
}

/** `custom/<skillId>` or `official/<officialSkillId>`: the id a badge sends. */
export function skillReference(locator: SkillLocator): string {
  return locator.source === 'official'
    ? `official/${locator.officialSkillId}`
    : `custom/${locator.skillId}`;
}

// ---------------------------------------------------------------------------
// Frozen context snapshot (member-facing reveal)
//
// Ordered items come back as `unknown[]` on the wire. Narrow them here the same
// way every other API payload is narrowed. Removals (`mode: 'removed'`) are
// part of the honest answer and must stay visible in the reveal UI.
// ---------------------------------------------------------------------------

export type SpecFrozenContextItemType =
  | 'app'
  | 'page'
  | 'scope'
  | 'account'
  | 'contact'
  | 'project'
  | 'signal'
  | 'view';

export type SpecFrozenContextItemMode = 'live' | 'pinned' | 'removed';

export interface SpecFrozenContextCarriedItem {
  type: SpecFrozenContextItemType;
  id?: string;
  label: string;
  mode: 'live' | 'pinned';
  dataset?: SpecContextViewDataset;
  route?: string;
  pathname?: string;
  entity?: {
    type: SpecContextEntityType;
    id: string;
    label: string;
    dataset?: SpecContextViewDataset;
  };
  view?: {
    type: 'view';
    id: string;
    label: string;
    dataset?: SpecContextViewDataset;
  };
}

/** A default the member switched OFF for this message. */
export interface SpecFrozenContextRemovedItem {
  type: 'app' | 'page';
  mode: 'removed';
}

export type SpecFrozenContextItem = SpecFrozenContextCarriedItem | SpecFrozenContextRemovedItem;

export interface SpecContextSnapshot {
  id: string;
  conversationId: string;
  createdBy: string | null;
  orderedItems: readonly SpecFrozenContextItem[];
  hash: string;
  createdAt: string;
}

const FROZEN_ITEM_TYPES = new Set<string>([
  'app',
  'page',
  'scope',
  'account',
  'contact',
  'project',
  'signal',
  'view',
]);
const FROZEN_CARRIED_MODES = new Set<string>(['live', 'pinned']);
const FROZEN_VIEW_DATASETS = new Set<string>(['account', 'contact', 'project']);

function readFrozenEntity(value: unknown): SpecFrozenContextCarriedItem['entity'] | undefined {
  const record = partRecord(value);
  if (!record) return undefined;
  const type = readString(record.type);
  const id = readString(record.id);
  const label = readString(record.label);
  if (!type || !FROZEN_ITEM_TYPES.has(type) || type === 'app' || type === 'page') return undefined;
  if (!id || label == null) return undefined;
  const dataset = readString(record.dataset);
  return {
    type: type as SpecContextEntityType,
    id,
    label,
    ...(dataset && FROZEN_VIEW_DATASETS.has(dataset)
      ? { dataset: dataset as SpecContextViewDataset }
      : {}),
  };
}

function readFrozenView(value: unknown): SpecFrozenContextCarriedItem['view'] | undefined {
  const record = partRecord(value);
  if (!record) return undefined;
  const id = readString(record.id);
  const label = readString(record.label);
  if (!id || label == null) return undefined;
  const dataset = readString(record.dataset);
  return {
    type: 'view',
    id,
    label,
    ...(dataset && FROZEN_VIEW_DATASETS.has(dataset)
      ? { dataset: dataset as SpecContextViewDataset }
      : {}),
  };
}

/** Narrow one wire item. Unreadable entries are dropped rather than guessed. */
export function readSpecFrozenContextItem(raw: unknown): SpecFrozenContextItem | null {
  const record = partRecord(raw);
  if (!record) return null;
  const type = readString(record.type);
  const mode = readString(record.mode);
  if (!type || !mode) return null;

  if (mode === 'removed') {
    if (type !== 'app' && type !== 'page') return null;
    return { type, mode: 'removed' };
  }

  if (!FROZEN_ITEM_TYPES.has(type) || !FROZEN_CARRIED_MODES.has(mode)) return null;
  const label = readString(record.label);
  if (label == null) return null;
  const id = readString(record.id) ?? undefined;
  const dataset = readString(record.dataset);
  const route = readString(record.route) ?? undefined;
  const pathname = readString(record.pathname) ?? undefined;
  const entity = readFrozenEntity(record.entity);
  const view = readFrozenView(record.view);

  return {
    type: type as SpecFrozenContextItemType,
    ...(id ? { id } : {}),
    label,
    mode: mode as 'live' | 'pinned',
    ...(dataset && FROZEN_VIEW_DATASETS.has(dataset)
      ? { dataset: dataset as SpecContextViewDataset }
      : {}),
    ...(route ? { route } : {}),
    ...(pathname ? { pathname } : {}),
    ...(entity ? { entity } : {}),
    ...(view ? { view } : {}),
  };
}

export function readSpecFrozenContextItems(raw: unknown): SpecFrozenContextItem[] {
  if (!Array.isArray(raw)) return [];
  const items: SpecFrozenContextItem[] = [];
  for (const entry of raw) {
    const item = readSpecFrozenContextItem(entry);
    if (item) items.push(item);
  }
  return items;
}

export function readSpecContextSnapshot(raw: unknown): SpecContextSnapshot {
  const record = partRecord(raw);
  if (!record) {
    throw new SpecClientError(
      502,
      'spec_context_snapshot_unreadable',
      'Spec could not read that context.',
    );
  }
  const id = readString(record.id);
  const conversationId = readString(record.conversationId);
  const hash = readString(record.hash);
  const createdAt = readString(record.createdAt);
  if (!id || !conversationId || !hash || !createdAt) {
    throw new SpecClientError(
      502,
      'spec_context_snapshot_unreadable',
      'Spec could not read that context.',
    );
  }
  const createdBy = readString(record.createdBy);
  return {
    id,
    conversationId,
    createdBy,
    orderedItems: readSpecFrozenContextItems(record.orderedItems),
    hash,
    createdAt,
  };
}

/** Short digest for display. The full hash is 64 hex chars. */
export function truncateSpecContextHash(hash: string, keep = 8): string {
  if (hash.length <= keep) return hash;
  return hash.slice(0, keep);
}

/** Project an envelope onto exactly the keys the server accepts. */
export function toSpecContextEnvelopeBody(envelope: SpecContextEnvelope): Record<string, unknown> {
  const focus = envelope.focus;
  return {
    version: SPEC_CONTEXT_ENVELOPE_VERSION,
    focus:
      focus === null
        ? null
        : focus.kind === 'scope'
          ? { kind: 'scope', dataset: focus.dataset }
          : { kind: 'record', type: focus.type, id: focus.id },
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * A failure from the Spec Worker, or a client-side refusal to call it.
 *
 * The message carries the HTTP status and the server's error CODE and nothing
 * else. Response bodies on this surface can quote a member's own prompt, and
 * an Error message is the single most likely thing to end up in a log line, a
 * Sentry breadcrumb, or a PostHog property.
 */
export class SpecClientError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message?: string) {
    super(message ?? `Spec request failed (${status} ${code}).`);
    this.name = 'SpecClientError';
    this.code = code;
    this.status = status;
  }
}

/** Reads the stable error code off either backend's failure. */
export function specErrorCode(err: unknown): string | undefined {
  if (err instanceof SpecClientError) return err.code;
  return apiErrorCode(err);
}

// ---------------------------------------------------------------------------
// SSE wire contract
//
// Verified against apps/spec/src/run-coordinator.ts `encodeSseFrame`:
//   id: <sequence>\nevent: <type>\ndata: <JSON>\n\n
// plus `retry: <ms>\n\n`, the `: keep-alive\n\n` comment, and a terminal
// `event: end` marker that deliberately carries NO id so it never advances a
// client cursor.
// ---------------------------------------------------------------------------

export const SPEC_STREAM_EVENT_TYPES = [
  'provider_started',
  'text_delta',
  'reasoning_delta',
  'tool_call',
  'tool_started',
  'usage',
  'cancel_requested',
  'run_parked',
  'turn_completed',
  'run_ended',
  'skill_draft_changed',
] as const;

export type SpecStreamEventType = (typeof SPEC_STREAM_EVENT_TYPES)[number];

const SPEC_STREAM_EVENT_TYPE_SET = new Set<string>(SPEC_STREAM_EVENT_TYPES);

const SPEC_RUN_STATUSES = [
  'created',
  'reserving',
  'running',
  'awaiting_approval',
  'completing',
  'completed',
  'failed',
  'canceled',
  'funds_exhausted',
  'policy_blocked',
] as const satisfies readonly SpecRunStatus[];

const SPEC_RUN_STATUS_SET = new Set<string>(SPEC_RUN_STATUSES);

const SPEC_TERMINAL_RUN_STATUSES = new Set<string>([
  'completed',
  'failed',
  'canceled',
  'funds_exhausted',
  'policy_blocked',
]);

export function isSpecRunTerminal(status: SpecRunStatus | null): boolean {
  return status != null && SPEC_TERMINAL_RUN_STATUSES.has(status);
}

export type SpecStreamFrame =
  | { kind: 'retry'; retryMs: number }
  | {
      kind: 'event';
      sequence: number | null;
      type: SpecStreamEventType;
      payload: Record<string, unknown>;
    }
  | { kind: 'end'; status: SpecRunStatus; errorCode: string | null }
  /**
   * A payload or terminal status this build cannot read: malformed JSON, a
   * non-object body, or an `end` marker naming a status that is not a run
   * status. Kept as a frame rather than dropped: the design requires degrading
   * visibly instead of guessing, and the sequence still has to advance the
   * cursor or a reconnect would replay it forever.
   */
  | { kind: 'unrecognized'; sequence: number | null; type: string }
  /**
   * A well-formed frame whose event TYPE this build does not know, which is
   * forward compatibility rather than a fault: the Worker started emitting a
   * frame that shipped after this tab loaded its JS.
   *
   * Deliberately NOT `unrecognized`. That kind degrades the run, which puts
   * "part of this response could not be displayed" in front of the member, and
   * an added event type is not a lost response. Conflating the two meant every
   * future event type could only ship by deploying the client first; keeping
   * them apart is what makes the event log additive.
   */
  | { kind: 'unknown_type'; sequence: number | null; type: string };

export interface SpecSseParser {
  push(chunk: string): SpecStreamFrame[];
}

interface SseDraft {
  id: string | null;
  event: string | null;
  data: string[];
  retry: number | null;
  seen: boolean;
}

function newDraft(): SseDraft {
  return { id: null, event: null, data: [], retry: null, seen: false };
}

function parseSequence(raw: string | null): number | null {
  if (raw == null) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function decodeDraft(draft: SseDraft): SpecStreamFrame | null {
  if (!draft.seen) return null;

  if (draft.event == null && draft.data.length === 0) {
    return draft.retry == null ? null : { kind: 'retry', retryMs: draft.retry };
  }

  const sequence = parseSequence(draft.id);
  const type = draft.event ?? 'message';
  let payload: unknown;
  try {
    payload = JSON.parse(draft.data.join('\n')) as unknown;
  } catch {
    return { kind: 'unrecognized', sequence, type };
  }
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return { kind: 'unrecognized', sequence, type };
  }
  const record = payload as Record<string, unknown>;

  if (type === 'end') {
    const status = record.status;
    // The terminal marker carries no id by design, so it never advances a
    // cursor even when it is the only thing a reconnect receives.
    if (typeof status !== 'string' || !SPEC_RUN_STATUS_SET.has(status)) {
      return { kind: 'unrecognized', sequence: null, type: 'end' };
    }
    const errorCode = record.errorCode;
    return {
      kind: 'end',
      status: status as SpecRunStatus,
      errorCode: typeof errorCode === 'string' ? errorCode : null,
    };
  }

  if (!SPEC_STREAM_EVENT_TYPE_SET.has(type)) {
    // Parsed cleanly, just not a type this build renders. See `unknown_type`.
    return { kind: 'unknown_type', sequence, type };
  }
  return { kind: 'event', sequence, type: type as SpecStreamEventType, payload: record };
}

/**
 * Incremental SSE reader. Framing only: it never interprets a payload beyond
 * `JSON.parse`, so the state machine below owns every product decision.
 */
export function createSpecSseParser(): SpecSseParser {
  let buffer = '';
  let draft = newDraft();

  return {
    push(chunk: string): SpecStreamFrame[] {
      buffer += chunk;
      const frames: SpecStreamFrame[] = [];
      let newline = buffer.indexOf('\n');
      while (newline !== -1) {
        const raw = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;

        if (line.length === 0) {
          const frame = decodeDraft(draft);
          draft = newDraft();
          if (frame) frames.push(frame);
        } else if (!line.startsWith(':')) {
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          const rest = colon === -1 ? '' : line.slice(colon + 1);
          const value = rest.startsWith(' ') ? rest.slice(1) : rest;
          if (field === 'id') {
            draft.id = value;
            draft.seen = true;
          } else if (field === 'event') {
            draft.event = value;
            draft.seen = true;
          } else if (field === 'data') {
            draft.data.push(value);
            draft.seen = true;
          } else if (field === 'retry') {
            const retryMs = Number(value);
            if (Number.isSafeInteger(retryMs) && retryMs > 0) {
              draft.retry = retryMs;
              draft.seen = true;
            }
          }
        }
        newline = buffer.indexOf('\n');
      }
      return frames;
    },
  };
}

/** Decode a Worker SSE body into frames. A partial trailing event is discarded. */
export async function* readSpecSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SpecStreamFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSpecSseParser();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const frame of parser.push(decoder.decode(value, { stream: true }))) yield frame;
    }
    for (const frame of parser.push(decoder.decode())) yield frame;
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

// ---------------------------------------------------------------------------
// Stream state machine
// ---------------------------------------------------------------------------

export type SpecStreamToolCallStatus = 'ok' | 'error' | 'unknown_tool' | 'summarized' | 'unknown';

export interface SpecStreamToolCall {
  /** `<runId>:<sequence>`, so a re-render or a replay resolves to one card. */
  id: string;
  tool: string;
  version: string;
  status: SpecStreamToolCallStatus;
  durationMs: number | null;
  resultBytes: number | null;
  /** Server-derived count for the step row. Never a value from the result. */
  summary?: string;
  /**
   * Trusted native-action proposal when the Worker streamed `actionProposalJson`.
   * Handed to assistant-ui as the tool-call `result` so the fallback card renders.
   */
  result?: unknown;
  isError?: boolean;
  /** The Worker streamed a proposal or question that did not parse; the settled part carries it. */
  proposalTruncated?: boolean;
  /** Record ids a search returned, for its result card; never fields. */
  projection?: SpecToolResultProjection;
  /** A project read: how many projects it returned, and the id when it was one (for photos). */
  projectsRead?: number;
  photoProjectId?: string;
  /** query_data hit the daily allowance: whether an upgrade would help. */
  queryLimit?: SpecQueryLimit;
}

export type SpecQueryLimit = 'upgrade' | 'wait';

function readQueryLimit(value: unknown): SpecQueryLimit | null {
  return value === 'upgrade' || value === 'wait' ? value : null;
}

/**
 * What one run metered, summed over the settlement checkpoints this client
 * actually received.
 *
 * `chargedMicros` is `landedCogsMicrousd * microcreditsPerCogsMicrousd`, which
 * is the amount `settleSpecCredits` charges the ledger EXACTLY (there is no
 * rounding in that multiplication, and a settlement above the reservation is
 * refused rather than rounded down). Tokens ride along because the same
 * checkpoint carries them and they are the only explanation of the number.
 */
export interface SpecRunUsage {
  /** Model steps whose checkpoint was seen. A parked run settles more than one. */
  steps: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  chargedMicros: number;
}

export interface SpecStreamState {
  runId: string;
  /** Highest canonical sequence applied. This is the `?after=` reconnect cursor. */
  lastSequence: number;
  /** Accumulated deltas. A truncated display hint, never canonical history. */
  text: string;
  toolCalls: readonly SpecStreamToolCall[];
  /** Reads the model began writing that have not finished; drawn running, after every settled part. */
  startedTools: readonly { id: string; tool: string }[];
  providerStarted: boolean;
  cancelRequested: boolean;
  status: SpecRunStatus | null;
  errorCode: string | null;
  /** The answer is complete and settled; the run's bookkeeping may not be. */
  turnCompleted: boolean;
  /** A terminal `run_ended` (or `end`) was seen. */
  ended: boolean;
  /** The `end` marker was seen: the stream is complete, do not reconnect. */
  closed: boolean;
  /** Something arrived that this build cannot render. Surfaced, never hidden. */
  degraded: boolean;
  /**
   * The replay buffer moved past this client's cursor, so some of the answer
   * was never received. Surfaced on the message, because the alternative is
   * presenting a hole in an answer as a complete one.
   */
  lostOutput: boolean;
  /** Server reconnect hint, in milliseconds. */
  retryMs: number;
  /**
   * How many times this run parked for an approval.
   *
   * Compared against the number of assistant messages canonical history holds
   * for the run, which is what tells the overlay whether history has caught up
   * to the stream. See `buildSpecThreadMessages`.
   */
  parks: number;
  /**
   * Reasoning, text, and tool-call parts in arrival order, folded the same
   * way the Worker folds its persisted turn parts, so the overlay and the
   * settled message agree on where each part sits and the chain of thought
   * does not regroup on settle. Tool-call entries index into `toolCalls`.
   */
  parts: readonly SpecStreamPart[];
  /**
   * `parts.length` at the most recent park.
   *
   * The accumulator is NOT cleared at park, because a client that never
   * refetched history has no other copy of the proposal card. Recording the
   * offset instead lets the overlay slice off the continuation exactly, with
   * no attempt to diff truncated display text against canonical parts.
   *
   * ONE offset is enough because a run can park at most once: the approval
   * continuation runs with `capabilities: { tools: 'none' }` and an inert
   * `onToolCall`, so it cannot propose a second action (pinned by "executes
   * once on approve wake, persists the tool result, and resumes the model" in
   * apps/spec/src/run-object.test.ts). If that ever changes, this needs one
   * offset per park, sliced at `parks[persistedAssistantCount - 1]`; slicing
   * from the LAST park would hide everything between the two.
   */
  parkedParts: number;
  /**
   * Settlement checkpoints seen so far, or null when none has arrived.
   *
   * Accumulated here rather than dispatched per frame because a run settles
   * once per model step and the total is what a member asked about. Only ever
   * shown once the run is terminal and no frames were lost; see the runtime
   * provider's `runUsage`.
   */
  usage: SpecRunUsage | null;
  /** Latest trusted draft invalidation, or null when this run has not written one. */
  skillDraftChanged: SkillDraftChangedEvent | null;
  /** Wall clock when this client began the stream; drives the timing chip. */
  startedAt: number;
  firstTextAt: number | null;
  endedAt: number | null;
  /** Accumulated reasoning-summary deltas. Never rendered as answer text. */
  reasoning: string;
  /** Each read's citation handles (ids only), so live `[S3]` markers can name their record. */
  cites: readonly SpecCitationHandle[];
}

export type SpecStreamPart =
  | { type: 'reasoning' | 'text'; text: string }
  | { type: 'tool-call'; index: number };

/** Matches SPEC_SSE_RETRY_MS in apps/spec/src/run-coordinator.ts. */
export const SPEC_STREAM_DEFAULT_RETRY_MS = 2_000;

export function initialSpecStreamState(
  runId: string,
  afterSequence = 0,
  now = Date.now(),
): SpecStreamState {
  return {
    runId,
    lastSequence: afterSequence,
    text: '',
    toolCalls: [],
    startedTools: [],
    providerStarted: false,
    cancelRequested: false,
    status: null,
    errorCode: null,
    turnCompleted: false,
    ended: false,
    closed: false,
    degraded: false,
    lostOutput: false,
    retryMs: SPEC_STREAM_DEFAULT_RETRY_MS,
    parks: 0,
    parts: [],
    parkedParts: 0,
    cites: [],
    usage: null,
    skillDraftChanged: null,
    startedAt: now,
    firstTextAt: null,
    endedAt: null,
    reasoning: '',
  };
}

/**
 * Rejoin a run whose replay buffer moved past this client's cursor.
 *
 * A gap is NOT the end of a run. The Durable Object keeps a bounded buffer
 * (256 frames), so a laptop that slept mid-answer wakes with a cursor below the
 * oldest retained frame while the run is still going. Abandoning there leaves
 * the composer enabled against a run the server still considers active, and the
 * next send is refused as `spec_run_active` until the member switches
 * conversations and back.
 *
 * So the cursor jumps to just below the oldest frame the buffer still holds,
 * which is the most that can still be recovered, and `lostOutput` records that
 * the text in between is gone. Settlement persists the complete assistant
 * message, so the terminal history refresh replaces this marked partial with
 * the canonical answer.
 */
export function markSpecStreamGap(state: SpecStreamState, resumeAfter: number): SpecStreamState {
  const cursor = Math.max(state.lastSequence, Math.max(0, resumeAfter));
  return { ...state, lastSequence: cursor, lostOutput: true };
}

/**
 * Where to resume after a `gap`. The DO reports the oldest sequence it still
 * holds; `?after=` is exclusive, so resume one below it to receive that frame.
 */
export function specGapResumeCursor(oldestSequence: number | null): number {
  if (oldestSequence == null || !Number.isSafeInteger(oldestSequence)) return 0;
  return Math.max(0, oldestSequence - 1);
}

const TOOL_CALL_STATUSES = new Set<string>(['ok', 'error', 'unknown_tool', 'summarized']);

/** Prose after a step's reads means the step is over: a read refused before running sends no result to clear it. */
function settledStarts(state: SpecStreamState): SpecStreamState['startedTools'] {
  return state.startedTools.length > 0 ? [] : state.startedTools;
}

/** Folds a delta into the last part of its kind, except across a park, which starts a new turn. */
function appendStreamText(
  state: SpecStreamState,
  kind: 'reasoning' | 'text',
  delta: string,
): readonly SpecStreamPart[] {
  const last = state.parts.at(-1);
  if (last && last.type === kind && state.parts.length > state.parkedParts) {
    return [...state.parts.slice(0, -1), { type: kind, text: last.text + delta }];
  }
  return [...state.parts, { type: kind, text: delta }];
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readWholeCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/**
 * One `usage` frame payload as `SpecUsageCheckpoint`, or null when this build
 * cannot turn it into a charge.
 *
 * Every field is required, because the charge is a PRODUCT: a missing or
 * unreadable conversion rate makes the credits unknowable rather than zero, and
 * printing zero on a money surface is the one wrong answer. The product is
 * bounds-checked for the same reason the ledger checks it before multiplying.
 */
function readUsageCheckpoint(payload: Record<string, unknown>): SpecRunUsage | null {
  const inputTokens = readWholeCount(payload.inputTokens);
  const cachedInputTokens = readWholeCount(payload.cachedInputTokens);
  const outputTokens = readWholeCount(payload.outputTokens);
  const landedCogsMicrousd = readWholeCount(payload.landedCogsMicrousd);
  const rate = readWholeCount(payload.microcreditsPerCogsMicrousd);
  if (
    inputTokens == null ||
    cachedInputTokens == null ||
    outputTokens == null ||
    landedCogsMicrousd == null ||
    rate == null ||
    rate <= 0
  ) {
    return null;
  }
  const chargedMicros = landedCogsMicrousd * rate;
  if (!Number.isSafeInteger(chargedMicros)) return null;
  return { steps: 1, inputTokens, cachedInputTokens, outputTokens, chargedMicros };
}

function addSpecRunUsage(current: SpecRunUsage | null, next: SpecRunUsage): SpecRunUsage {
  if (!current) return next;
  return {
    steps: current.steps + next.steps,
    inputTokens: current.inputTokens + next.inputTokens,
    cachedInputTokens: current.cachedInputTokens + next.cachedInputTokens,
    outputTokens: current.outputTokens + next.outputTokens,
    chargedMicros: current.chargedMicros + next.chargedMicros,
  };
}

/**
 * Fold one frame into the stream state.
 *
 * Returns the SAME object when the frame changes nothing, so a duplicate
 * replayed after reconnect cannot re-render the thread. Any frame at or below
 * the cursor is suppressed, which covers both a replayed frame and an
 * out-of-order arrival.
 */
export function applySpecStreamFrame(
  state: SpecStreamState,
  frame: SpecStreamFrame,
  now = Date.now(),
): SpecStreamState {
  if (frame.kind === 'retry') {
    return frame.retryMs === state.retryMs ? state : { ...state, retryMs: frame.retryMs };
  }

  if (frame.kind === 'end') {
    return {
      ...state,
      closed: true,
      ended: true,
      status: frame.status,
      errorCode: frame.errorCode ?? state.errorCode,
      endedAt: state.endedAt ?? now,
    };
  }

  if (frame.sequence !== null && frame.sequence <= state.lastSequence) return state;
  const lastSequence = frame.sequence ?? state.lastSequence;

  if (frame.kind === 'unrecognized') {
    return { ...state, lastSequence, degraded: true };
  }

  // Advance the cursor and say nothing. A frame type this build does not know
  // is not a hole in the answer, so it must not raise `degraded`.
  if (frame.kind === 'unknown_type') {
    return lastSequence === state.lastSequence ? state : { ...state, lastSequence };
  }

  switch (frame.type) {
    case 'text_delta': {
      const text = frame.payload.text;
      if (typeof text !== 'string') return { ...state, lastSequence, degraded: true };
      return {
        ...state,
        lastSequence,
        text: state.text + text,
        parts: appendStreamText(state, 'text', text),
        firstTextAt: state.firstTextAt ?? now,
        startedTools: settledStarts(state),
      };
    }
    case 'reasoning_delta': {
      const text = frame.payload.text;
      if (typeof text !== 'string') return { ...state, lastSequence, degraded: true };
      return {
        ...state,
        lastSequence,
        reasoning: state.reasoning + text,
        parts: appendStreamText(state, 'reasoning', text),
        startedTools: settledStarts(state),
      };
    }
    case 'tool_call': {
      const rawStatus = frame.payload.status;
      const proposalJson = readString(frame.payload.actionProposalJson);
      const questionJson = readString(frame.payload.questionJson);
      const presentJson = readString(frame.payload.presentJson);
      let result: unknown;
      let proposalTruncated = false;
      if (proposalJson) {
        try {
          result = JSON.parse(proposalJson);
        } catch {
          // Truncated or corrupt live preview (frame budget). Keep the
          // tool-call status card; the full proposal arrives on settle.
          result = undefined;
          proposalTruncated = true;
        }
      } else if (questionJson) {
        // An unreadable question waits for the persisted part, like a cut-off proposal.
        result = readSpecQuestionSet(parseJson(questionJson)) ?? undefined;
        proposalTruncated = result === undefined;
      } else if (presentJson) {
        // Same for a layout: the saved part carries it if the frame did not parse.
        result = readSpecPresent(parseJson(presentJson)) ?? undefined;
        proposalTruncated = result === undefined;
      }
      const toolCallId = readString(frame.payload.toolCallId) ?? `${state.runId}:${lastSequence}`;
      const summary = readString(frame.payload.summary);
      const projectsRead = readWholeCount(frame.payload.projectsRead);
      const photoProjectId = readString(frame.payload.photoProjectId);
      const queryLimit = readQueryLimit(frame.payload.queryLimit);
      const resultIdsJson = readString(frame.payload.resultIdsJson);
      const projection = resultIdsJson ? readToolResultProjection(parseJson(resultIdsJson)) : null;
      const call: SpecStreamToolCall = {
        id: toolCallId,
        tool: readString(frame.payload.tool) ?? 'tool',
        version: readString(frame.payload.version) ?? '',
        status:
          typeof rawStatus === 'string' && TOOL_CALL_STATUSES.has(rawStatus)
            ? (rawStatus as SpecStreamToolCallStatus)
            : 'unknown',
        durationMs: readCount(frame.payload.durationMs),
        resultBytes: readCount(frame.payload.resultBytes),
        ...(summary === null ? {} : { summary }),
        ...(projection ? { projection } : {}),
        ...(projectsRead ? { projectsRead } : {}),
        ...(projectsRead === 1 && photoProjectId ? { photoProjectId } : {}),
        ...(queryLimit ? { queryLimit } : {}),
        ...(result !== undefined
          ? {
              result,
              isError:
                typeof result === 'object' &&
                result !== null &&
                (result as { state?: unknown }).state === 'blocked',
            }
          : {}),
        ...(proposalTruncated ? { proposalTruncated: true } : {}),
      };
      const cites = readSpecCites(frame.payload.citesJson);
      return {
        ...state,
        lastSequence,
        toolCalls: [...state.toolCalls, call],
        startedTools: state.startedTools.filter((started) => started.id !== toolCallId),
        parts: [...state.parts, { type: 'tool-call', index: state.toolCalls.length }],
        ...(cites.length > 0 ? { cites: [...state.cites, ...cites] } : {}),
      };
    }
    case 'tool_started': {
      const id = readString(frame.payload.toolCallId);
      const tool = readString(frame.payload.tool);
      const known =
        state.toolCalls.some((call) => call.id === id) ||
        state.startedTools.some((started) => started.id === id);
      if (!id || !tool || known) return { ...state, lastSequence };
      return { ...state, lastSequence, startedTools: [...state.startedTools, { id, tool }] };
    }
    case 'provider_started':
      return { ...state, lastSequence, providerStarted: true };
    case 'cancel_requested':
      return { ...state, lastSequence, cancelRequested: true };
    case 'usage': {
      // A settlement checkpoint. Never rendered inline -- it is billing data,
      // not conversation -- but accumulated so the per-message usage menu can
      // answer "what did this answer cost" once the run is over. An
      // unreadable payload advances the cursor and adds nothing, because a
      // partial total is worse on a money surface than no total.
      const checkpoint = readUsageCheckpoint(frame.payload);
      return checkpoint
        ? { ...state, lastSequence, usage: addSpecRunUsage(state.usage, checkpoint) }
        : { ...state, lastSequence };
    }
    case 'run_parked':
      // The Worker has persisted everything streamed so far as the proposal
      // card and is now waiting on a member decision. Record where the
      // accumulator stood; `lostOutput` and `degraded` are deliberately left
      // alone, because the park append is best-effort and clearing an honesty
      // flag here would claim a completeness this client cannot verify.
      return {
        ...state,
        lastSequence,
        parks: state.parks + 1,
        parkedParts: state.parts.length,
        startedTools: [],
      };
    // The answer is done. `run_ended` still closes the run and carries status,
    // but the clock stops here: what follows is bookkeeping nobody waits on.
    case 'turn_completed':
      return { ...state, lastSequence, turnCompleted: true, endedAt: state.endedAt ?? now };
    case 'run_ended': {
      const status = frame.payload.status;
      if (typeof status !== 'string' || !SPEC_RUN_STATUS_SET.has(status)) {
        return {
          ...state,
          lastSequence,
          ended: true,
          degraded: true,
          endedAt: state.endedAt ?? now,
        };
      }
      const errorCode = frame.payload.errorCode;
      return {
        ...state,
        lastSequence,
        ended: true,
        status: status as SpecRunStatus,
        errorCode: typeof errorCode === 'string' ? errorCode : null,
        endedAt: state.endedAt ?? now,
      };
    }
    case 'skill_draft_changed': {
      // Invalidation only: never conversation text, never a publish mutation.
      const event = parseSkillDraftChanged(frame.payload);
      return { ...state, lastSequence, skillDraftChanged: event };
    }
  }
}

/** When a rejoined run began, on the server's clock; never after `now`, so skew cannot show a negative clock. */
export function specRunStartedAt(run: { createdAt: string }, now = Date.now()): number {
  const created = Date.parse(run.createdAt);
  return Number.isFinite(created) ? Math.min(created, now) : now;
}

/** `ThreadMessageLike.metadata.timing` for the live overlay message. */
function streamTiming(
  stream: SpecStreamState,
): NonNullable<ThreadMessageLike['metadata']>['timing'] {
  return {
    streamStartTime: stream.startedAt,
    ...(stream.firstTextAt != null
      ? { firstTokenTime: stream.firstTextAt - stream.startedAt }
      : {}),
    ...(stream.endedAt != null ? { totalStreamTime: stream.endedAt - stream.startedAt } : {}),
    ...(stream.usage ? { tokenCount: stream.usage.outputTokens } : {}),
    totalChunks: 0,
    toolCallCount: stream.toolCalls.length,
  };
}

export interface SpecRunNotice {
  /** What the member is told. */
  message: string;
  /** Whether sending the same thing again could plausibly work. */
  retryable: boolean;
}

/**
 * What each failure reason says to the member.
 *
 * Exhaustive over the shared vocabulary so a new code cannot quietly inherit
 * "could not finish". `retryable` is the distinction that matters more than the
 * wording: offering a retry for a refusal or an empty balance is a lie.
 */
const NOTICE_FOR_ERROR_CODE: Readonly<Record<SpecRunErrorCode, SpecRunNotice>> = {
  provider_timeout: { message: 'Spec took too long to finish this response.', retryable: true },
  provider_error: { message: 'The model had trouble finishing this response.', retryable: true },
  provider_overloaded: { message: 'The model is busy right now.', retryable: true },
  provider_refused: { message: 'The model declined to answer this one.', retryable: false },
  // A rerun usually fits: how long the model thinks varies from run to run.
  output_limit: {
    message: 'This answer ran longer than Spec allows. Try again, or ask for a shorter answer.',
    retryable: true,
  },
  context_overflow: {
    message: 'This conversation is too long for Spec to answer in one response.',
    retryable: false,
  },
  model_unavailable: { message: 'No Spec model is available right now.', retryable: false },
  funds_exhausted: { message: 'This workspace is out of credits.', retryable: false },
  payment_required: { message: 'This workspace is out of credits.', retryable: false },
  policy_blocked: { message: 'Workspace policy blocked this request.', retryable: false },
  run_expired: { message: 'Spec ran out of time on this response.', retryable: true },
  run_abandoned: { message: 'Spec lost track of this response.', retryable: true },
  lease_lost: { message: 'Spec lost track of this response.', retryable: true },
  client_disconnected: {
    message: 'The connection dropped before Spec finished.',
    retryable: true,
  },
  internal_error: { message: 'Something went wrong on our end.', retryable: true },
  // Its own status, and never rendered as a failure.
  canceled_by_user: { message: 'You stopped this response.', retryable: true },
};

const UNKNOWN_FAILURE: SpecRunNotice = {
  message: 'Spec could not finish this response.',
  retryable: true,
};

/**
 * Member-facing notice for a terminal run, or null when nothing needs saying.
 *
 * The code is the better witness than the status: `funds_exhausted` and
 * `policy_blocked` are their own statuses, but every other reason arrives as a
 * plain `failed` and only the code says which one.
 */
export function specRunNotice(
  status: SpecRunStatus | null,
  errorCode: string | null,
): SpecRunNotice | null {
  if (status !== 'failed' && status !== 'funds_exhausted' && status !== 'policy_blocked') {
    return null;
  }
  if (isSpecRunErrorCode(errorCode)) return NOTICE_FOR_ERROR_CODE[errorCode];
  // A status that is its own reason still answers when the code is missing.
  if (status === 'funds_exhausted') return NOTICE_FOR_ERROR_CODE.funds_exhausted;
  if (status === 'policy_blocked') return NOTICE_FOR_ERROR_CODE.policy_blocked;
  return UNKNOWN_FAILURE;
}

/** The sentence plus its retry guidance, which is the whole notice a member reads. */
export function specRunNoticeText(notice: SpecRunNotice): string {
  return notice.retryable ? `${notice.message} Try again.` : notice.message;
}

/**
 * What a member quotes back when they report a failure: the code that names the
 * reason and enough of the run id to find the row. Diagnosing this before meant
 * querying the database, because the screen said nothing either one could use.
 */
export function specRunReference(run: { id: string; errorCode: string | null }): string {
  const code = isSpecRunErrorCode(run.errorCode) ? run.errorCode : 'unknown';
  return `${code} · ${run.id.slice(0, 8)}`;
}

// ---------------------------------------------------------------------------
// Persisted messages -> Assistant UI messages
// ---------------------------------------------------------------------------

export const SPEC_UNRENDERABLE_NOTICE =
  'This message cannot be displayed by this version of Parcel.';
export const SPEC_STREAM_DEGRADED_NOTICE = 'Part of this response could not be displayed.';
/** Shown above a partial answer the client rejoined after a replay gap. */
export const SPEC_STREAM_GAP_NOTICE =
  'Earlier output was missed while reconnecting. The full answer appears when the response finishes.';

type ThreadContent = Exclude<ThreadMessageLike['content'], string>;
type ThreadStatus = NonNullable<ThreadMessageLike['status']>;

interface MutableTextPart {
  type: 'text';
  text: string;
}

interface MutableReasoningPart {
  type: 'reasoning';
  text: string;
  status?: { type: 'running' | 'complete' };
}

interface MutableToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args?: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
}

type MutablePart = MutableTextPart | MutableReasoningPart | MutableToolCallPart;

function asContent(parts: readonly MutablePart[]): ThreadContent {
  return parts as unknown as ThreadContent;
}

function messageStatus(status: SpecMessageStatus): ThreadStatus {
  switch (status) {
    case 'pending':
      return { type: 'running' };
    case 'complete':
      return { type: 'complete', reason: 'stop' };
    case 'canceled':
      return { type: 'incomplete', reason: 'cancelled' };
    case 'failed':
      return { type: 'incomplete', reason: 'error' };
  }
}

function messageMetadata(message: SpecMessage): ThreadMessageLike['metadata'] {
  const feedback = message.feedback ?? null;
  return {
    ...(feedback
      ? { submittedFeedback: { type: feedback.rating === 'up' ? 'positive' : 'negative' } }
      : {}),
    custom: {
      specMessageId: message.id,
      runId: message.runId,
      clientMessageId: message.clientMessageId,
      // The frozen snapshot this message was sent with. Task 3 reads it to
      // reveal what the model saw at send time.
      contextSnapshotId: message.contextSnapshotId,
      sequence: message.sequence,
      partsVersion: message.partsVersion,
      specAttachedSkills: message.attachedSkills ?? [],
      feedbackReason: feedback?.reason ?? null,
      specFiles: specFilesOf(message.parts),
      specSources: specSourcesOf(message.parts),
    },
  };
}

/** The records the answer's `[n]` markers cite, ids only; the browser looks them up. */
function specSourcesOf(parts: readonly unknown[]): SpecSource[] {
  return parts.flatMap((part) => readSpecSources(part));
}

/** The message's file parts; the bubble draws them from the text's chips, Edit hides for them. */
function specFilesOf(parts: readonly unknown[]): SpecFilePart[] {
  return parts.flatMap((part) => {
    const file = readSpecFilePart(part);
    return file ? [file] : [];
  });
}

function partRecord(part: unknown): Record<string, unknown> | null {
  return typeof part === 'object' && part !== null && !Array.isArray(part)
    ? (part as Record<string, unknown>)
    : null;
}

function toolCallIdOf(record: Record<string, unknown>): string | null {
  return readString(record.toolCallId) ?? readString(record.toolId);
}

/** Fill a previously emitted tool-call part with the result that answers it. */
function applyToolResult(
  record: Record<string, unknown>,
  calls: Map<string, MutableToolCallPart>,
): MutableToolCallPart | null {
  const toolCallId = toolCallIdOf(record);
  if (!toolCallId) return null;
  const existing = calls.get(toolCallId);
  const target: MutableToolCallPart = existing ?? {
    type: 'tool-call',
    toolCallId,
    toolName: readString(record.toolName) ?? 'tool',
  };
  target.result = record.result ?? record.output ?? null;
  target.isError = record.isError === true;
  if (!existing) {
    calls.set(toolCallId, target);
    // An orphan result: the call it answers is not in this page of history.
    // Shown as a resolved tool card rather than dropped, so the thread never
    // silently loses evidence that a read ran.
    return target;
  }
  return null;
}

function mapMessageParts(
  message: SpecMessage,
  calls: Map<string, MutableToolCallPart>,
): { content: MutablePart[]; dropped: number } {
  const content: MutablePart[] = [];
  let dropped = 0;

  for (const raw of message.parts) {
    const record = partRecord(raw);
    if (!record) {
      dropped += 1;
      continue;
    }
    const type = record.type;
    if (type === 'text') {
      if (typeof record.text === 'string') content.push({ type: 'text', text: record.text });
      else dropped += 1;
      continue;
    }
    if (type === 'reasoning') {
      if (typeof record.text === 'string') content.push({ type: 'reasoning', text: record.text });
      else dropped += 1;
      continue;
    }
    if (type === 'tool-call') {
      const toolCallId = toolCallIdOf(record);
      if (!toolCallId) {
        dropped += 1;
        continue;
      }
      const part: MutableToolCallPart = {
        type: 'tool-call',
        toolCallId,
        toolName: readString(record.toolName) ?? 'tool',
      };
      const args = partRecord(record.args ?? record.input);
      if (args) part.args = args;
      // Result may live on the tool-call itself (Spec persists native-action
      // proposals that way) or arrive later via a folded tool-result part.
      if ('result' in record) part.result = record.result;
      if (record.isError === true) part.isError = true;
      calls.set(toolCallId, part);
      content.push(part);
      continue;
    }
    if (type === 'tool-result') {
      const orphan = applyToolResult(record, calls);
      if (orphan) content.push(orphan);
      continue;
    }
    // Files ride on the metadata (`specFiles`); the chip in the text is what renders.
    if (type === 'file' && readSpecFilePart(record)) continue;
    // Sources ride on the metadata too (`specSources`); the markers and the row draw them.
    if (type === SPEC_SOURCES_PART_TYPE) continue;
    dropped += 1;
  }

  return { content, dropped };
}

function projectionOf(part: MutablePart): SpecToolResultProjection | null {
  return part.type === 'tool-call' ? (asToolStep(part.result)?.projection ?? null) : null;
}

function recordsKey(projection: SpecToolResultProjection): string {
  return `${projection.entity}:${[...projection.ids].sort().join(',')}`;
}

export const SPEC_PHOTOS_RESULT_TYPE = 'spec-photos';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A step's project read: the count, and the one project's id. */
function projectReadOf(part: MutablePart): { count: number; projectId: string | null } | null {
  if (part.type !== 'tool-call') return null;
  const result = part.result as
    | { type?: unknown; projectsRead?: unknown; photoProjectId?: unknown }
    | undefined;
  if (result?.type !== 'spec-tool-step') return null;
  const count = typeof result.projectsRead === 'number' ? result.projectsRead : 0;
  if (count < 1) return null;
  const id = result.photoProjectId;
  return {
    count,
    projectId: count === 1 && typeof id === 'string' && UUID_RE.test(id) ? id : null,
  };
}

/** The last project read, when it returned one project and every one-project read named that one. */
function photoReadOf(content: readonly unknown[]): { index: number; projectId: string } | null {
  let last: { index: number; projectId: string | null } | null = null;
  const named = new Set<string | null>();
  for (const [index, part] of content.entries()) {
    const read = projectReadOf(part as MutablePart);
    if (!read) continue;
    last = { index, projectId: read.projectId };
    if (read.count === 1) named.add(read.projectId);
  }
  return last?.projectId && named.size === 1
    ? { index: last.index, projectId: last.projectId }
    : null;
}

/** The daily query_data allowance a turn ran into, from its last step that hit it. */
export function queryLimitOf(content: readonly unknown[]): SpecQueryLimit | null {
  for (let index = content.length - 1; index >= 0; index -= 1) {
    const part = content[index] as MutablePart;
    if (part?.type !== 'tool-call') continue;
    const result = part.result as { type?: unknown; queryLimit?: unknown } | undefined;
    if (result?.type !== 'spec-tool-step') continue;
    const limit = readQueryLimit(result.queryLimit);
    if (limit) return limit;
  }
  return null;
}

/** The project an answer is about, when the turn read just that one. */
export function photoProjectOf(content: readonly unknown[]): string | null {
  return photoReadOf(content)?.projectId ?? null;
}

// The tag only: this runs on every frame, and the renderer reads the layout itself.
function isPresentPart(part: MutablePart): boolean {
  return (
    part.type === 'tool-call' &&
    (part.result as { type?: unknown } | undefined)?.type === SPEC_PRESENT_RESULT_TYPE
  );
}

/** The last shown search per entity gets a records part before the answer text, unless `drawn`
 * already has it, and the last `present` layout moves there too; `holdTrailing` keeps a live
 * turn's cards back until its answer starts. */
export function placeSearchRecords(
  content: readonly MutablePart[],
  drawn: Set<string> = new Set(),
  holdTrailing = false,
): MutablePart[] {
  const lastByEntity = new Map<string, number>();
  content.forEach((part, index) => {
    const projection = projectionOf(part);
    if (projection) lastByEntity.set(projection.entity, index);
  });
  for (const [entity, index] of lastByEntity) {
    const projection = projectionOf(content[index] as MutablePart);
    if (!projection) continue;
    if (!projection.shown) {
      lastByEntity.delete(entity);
      continue;
    }
    const key = recordsKey(projection);
    if (drawn.has(key)) lastByEntity.delete(entity);
    else drawn.add(key);
  }
  // A later layout replaces an earlier one, as in the worker.
  let lastPresent = -1;
  content.forEach((part, index) => {
    if (isPresentPart(part)) lastPresent = index;
  });
  // An answer about one project opens with its photos, placed and held like a layout.
  const photo = photoReadOf(content);
  if (lastByEntity.size === 0 && lastPresent < 0 && !photo) return [...content];
  const shown = new Set(lastByEntity.values());
  const out: MutablePart[] = [];
  let waiting: MutablePart[] = [];
  content.forEach((part, index) => {
    if (part.type === 'text' && waiting.length > 0) {
      out.push(...waiting);
      waiting = [];
    }
    if (isPresentPart(part)) {
      if (index === lastPresent) waiting.push(part);
      return;
    }
    out.push(part);
    const projection = shown.has(index) ? projectionOf(part) : null;
    if (projection && part.type === 'tool-call') {
      waiting.push({
        type: 'tool-call',
        toolCallId: `${part.toolCallId}:records`,
        toolName: part.toolName,
        result: { type: 'spec-tool-records', projection },
      });
    }
    if (photo && index === photo.index && part.type === 'tool-call') {
      waiting.push({
        type: 'tool-call',
        toolCallId: `${part.toolCallId}:photos`,
        toolName: 'spec_photos',
        result: { type: SPEC_PHOTOS_RESULT_TYPE, projectId: photo.projectId },
      });
    }
  });
  return holdTrailing ? out : [...out, ...waiting];
}

function unrenderableMessage(message: SpecMessage): ThreadMessageLike {
  return {
    id: message.id,
    role: message.role === 'user' ? 'user' : 'assistant',
    content: asContent([{ type: 'text', text: SPEC_UNRENDERABLE_NOTICE }]),
    createdAt: new Date(message.createdAt),
    metadata: messageMetadata(message),
  };
}

function threadRunId(entry: ThreadMessageLike): string | undefined {
  const value = entry.metadata?.custom?.runId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function threadContentParts(content: ThreadMessageLike['content']): MutablePart[] {
  if (typeof content === 'string') return [{ type: 'text', text: content }];
  return Array.isArray(content) ? ([...content] as MutablePart[]) : [];
}

function threadCustom(metadata: ThreadMessageLike['metadata']): Record<string, unknown> {
  const custom = metadata?.custom;
  return custom && typeof custom === 'object' && !Array.isArray(custom)
    ? { ...(custom as Record<string, unknown>) }
    : {};
}

/**
 * Last-wins on metadata and on custom keys. `specRunRef` needs no special case:
 * `decorateWithRun` is the only thing that sets it and runs after this merge.
 */
function mergeThreadMetadata(
  earlier: ThreadMessageLike['metadata'],
  later: ThreadMessageLike['metadata'],
): ThreadMessageLike['metadata'] {
  const before = threadCustom(earlier);
  const after = threadCustom(later);
  // A parked row and its continuation are one answer; the continuation runs without tools and saves
  // no sources (the worker pins this), so the numbers never collide.
  const specSources = [before.specSources, after.specSources].flatMap((value) =>
    Array.isArray(value) ? value : [],
  );
  return { ...earlier, ...later, custom: { ...before, ...after, specSources } };
}

function mergeAssistantThreadMessages(
  earlier: ThreadMessageLike,
  later: ThreadMessageLike,
): ThreadMessageLike {
  return {
    ...later,
    createdAt: earlier.createdAt,
    content: asContent([
      ...threadContentParts(earlier.content),
      ...threadContentParts(later.content),
    ]),
    metadata: mergeThreadMetadata(earlier.metadata, later.metadata),
  };
}

/**
 * One run that parked for approval persists the proposal and the continuation
 * as two assistant rows. Fold the ones that share a `runId` into a single
 * turn so the member sees one Spec header, one action bar, and one timing.
 *
 * Adjacency is on the *rendered* list. A `tool` row whose result folded into
 * an earlier call never becomes a turn, so it must not split the merge — that
 * is the stored shape of an approved park. A user message does split it.
 */
function coalesceAdjacentAssistantTurns(
  messages: readonly ThreadMessageLike[],
): ThreadMessageLike[] {
  const out: ThreadMessageLike[] = [];
  for (const entry of messages) {
    const previous = out.at(-1);
    const previousRunId = previous ? threadRunId(previous) : undefined;
    const runId = threadRunId(entry);
    if (
      previous &&
      previous.role === 'assistant' &&
      entry.role === 'assistant' &&
      previousRunId !== undefined &&
      previousRunId === runId
    ) {
      out[out.length - 1] = mergeAssistantThreadMessages(previous, entry);
      continue;
    }
    out.push(entry);
  }
  return out;
}

/**
 * Map persisted Spec messages onto Assistant UI messages.
 *
 * Four rules carry weight:
 *
 *  - An unknown `partsVersion` is rendered as a visible notice, never guessed
 *    at. A build that renders a schema it does not understand is a build that
 *    shows the member something the server did not say.
 *  - A `tool` role message has no Assistant UI equivalent; its `tool-result`
 *    parts are folded into the assistant `tool-call` they answer.
 *  - Ordering is by persisted `sequence`, not array order.
 *  - Adjacent assistant messages that share a `runId` render as one turn. A
 *    park-and-continue run stores two rows on purpose (the proposal has to
 *    survive a reload); presenting them as two answers is the bug.
 */
export function specMessagesToThreadMessages(
  messages: readonly SpecMessage[],
): ThreadMessageLike[] {
  const ordered = [...messages].sort((left, right) => left.sequence - right.sequence);
  const calls = new Map<string, MutableToolCallPart>();
  const out: ThreadMessageLike[] = [];

  for (const message of ordered) {
    if (message.partsVersion !== SPEC_MESSAGE_PARTS_VERSION) {
      out.push(unrenderableMessage(message));
      continue;
    }

    const { content, dropped } = mapMessageParts(message, calls);

    if (message.role === 'tool') {
      // Results that landed on an existing call already mutated it in place.
      if (content.length === 0) continue;
      out.push({
        id: message.id,
        role: 'assistant',
        content: asContent(content),
        createdAt: new Date(message.createdAt),
        status: messageStatus(message.status),
        metadata: messageMetadata(message),
      });
      continue;
    }

    const renderable =
      content.length > 0 || dropped === 0
        ? content
        : [{ type: 'text', text: SPEC_UNRENDERABLE_NOTICE } satisfies MutableTextPart];

    const entry: ThreadMessageLike = {
      id: message.id,
      role: message.role,
      content: asContent(renderable),
      createdAt: new Date(message.createdAt),
      metadata: messageMetadata(message),
      ...(message.role === 'assistant' ? { status: messageStatus(message.status) } : {}),
    };
    out.push(entry);
  }

  return placeRecordsInThread(coalesceAdjacentAssistantTurns(out));
}

function placeRecordsInThread(
  messages: ThreadMessageLike[],
  drawn: Set<string> = new Set(),
): ThreadMessageLike[] {
  return messages.map((entry) =>
    entry.role === 'assistant' && Array.isArray(entry.content)
      ? {
          ...entry,
          content: asContent(placeSearchRecords(threadContentParts(entry.content), drawn)),
        }
      : entry,
  );
}

/** Records keys already drawn in these messages, so the live turn does not draw them twice. */
function drawnRecords(messages: readonly ThreadMessageLike[]): Set<string> {
  const drawn = new Set<string>();
  for (const entry of messages) {
    for (const part of threadContentParts(entry.content)) {
      const records = part.type === 'tool-call' ? asToolRecords(part.result) : null;
      if (records) drawn.add(recordsKey(records.projection));
    }
  }
  return drawn;
}

// ---------------------------------------------------------------------------
// Thread composition
// ---------------------------------------------------------------------------

/**
 * A send in flight. Held verbatim so a retry replays the SAME idempotency key
 * and the SAME frozen context: re-sending one id with different parts is
 * rejected by the API as two messages, and a message's context must not change
 * because the user navigated after pressing send.
 */
export interface SpecPendingSend {
  clientMessageId: string;
  /**
   * The conversation this send belongs to, or null when it was composed in a
   * new thread whose conversation does not exist yet.
   *
   * A send belongs to a conversation, not to a viewing session. This is what
   * lets a resolution that arrives after the member navigated be matched
   * against where they are now, instead of against whether a particular
   * in-memory send record happens to still be around.
   */
  conversationId: string | null;
  parts: readonly SpecOutgoingPart[];
  context: SpecContextEnvelope;
  skills: readonly string[];
  connectionsOff: readonly string[];
  webOff?: boolean;
  /** Only when the member chose Fast; frozen at send like the rest. */
  modelClass?: 'fast';
  createdAt: string;
}

export interface SpecThreadInput {
  messages: readonly SpecMessage[];
  /** Runs referenced by those messages, so a terminal failure keeps its reason. */
  runs?: readonly SpecRunSummary[];
  pending?: SpecPendingSend | null;
  stream?: SpecStreamState | null;
  /** A send is in flight and its run is not accepted yet; Spec's reply shows from this moment. */
  sending?: boolean;
  /** When that send was pressed, so the reply's clock starts there. */
  sentAt?: number | null;
  /** Durations the member watched live, by run id; they outrank the server's measure so the time never jumps. */
  runDurations?: Readonly<Record<string, number>>;
}

/** The answer is done. `ended` additionally means the run's bookkeeping closed. */
function turnFinished(stream: SpecStreamState): boolean {
  return stream.ended || stream.turnCompleted;
}

function streamStatus(stream: SpecStreamState): ThreadStatus {
  if (!stream.ended) {
    // A stop the member asked for reads as stopped straight away. The run takes
    // seconds more to wind down, and calling that "running" is the same lie the
    // spinner used to tell.
    if (stream.cancelRequested) return { type: 'incomplete', reason: 'cancelled' };
    // Complete once the turn is; the run may still be naming the thread.
    return stream.turnCompleted ? { type: 'complete', reason: 'stop' } : { type: 'running' };
  }
  // Ended: the run's own verdict outranks the optimistic flag, so a Stop that
  // lost the race to a finished answer does not claim the member stopped it.
  if (stream.status === 'completed') return { type: 'complete', reason: 'stop' };
  if (stream.status === 'canceled') return { type: 'incomplete', reason: 'cancelled' };
  // Only when the run named NO status: a cancel that raced a real failure must
  // keep the failure, which carries the reason and the remedy.
  if (stream.cancelRequested && stream.status === null) {
    return { type: 'incomplete', reason: 'cancelled' };
  }
  const notice = specRunNotice(stream.status, stream.errorCode) ?? UNKNOWN_FAILURE;
  return { type: 'incomplete', reason: 'error', error: specRunNoticeText(notice) };
}

function decorateWithRun(
  entry: ThreadMessageLike,
  message: SpecMessage | undefined,
  runsById: Map<string, SpecRunSummary>,
  runDurations: Readonly<Record<string, number>> = {},
): ThreadMessageLike {
  if (!message || message.role !== 'assistant') return entry;
  const record = runsById.get(message.runId);
  if (!record) return entry;

  const start = Date.parse(record.createdAt);
  // `answeredAt`, not `completedAt`: the run row closes after the title step,
  // which runs once the answer is already settled and nobody is waiting on it.
  const finished = record.answeredAt ?? record.completedAt;
  const end = finished ? Date.parse(finished) : Number.NaN;
  const watched = runDurations[message.runId];
  const elapsed =
    watched !== undefined
      ? watched
      : Number.isFinite(end) && end >= start
        ? end - start
        : undefined;
  const timing: MessageTiming | undefined = Number.isFinite(start)
    ? {
        streamStartTime: start,
        ...(elapsed === undefined ? {} : { totalStreamTime: elapsed }),
        totalChunks: 0,
        toolCallCount: 0,
      }
    : undefined;
  const withTiming = timing ? { ...entry, metadata: { ...entry.metadata, timing } } : entry;

  if (record.status === 'canceled') {
    return { ...withTiming, status: { type: 'incomplete', reason: 'cancelled' } };
  }
  const notice = specRunNotice(record.status, record.errorCode);
  if (!notice) return withTiming;
  return {
    ...withTiming,
    metadata: {
      ...withTiming.metadata,
      custom: { ...withTiming.metadata?.custom, specRunRef: specRunReference(record) },
    },
    status: { type: 'incomplete', reason: 'error', error: specRunNoticeText(notice) },
  };
}

/**
 * The message list the ExternalStore adapter hands to Assistant UI.
 *
 * Canonical history always wins. The optimistic user message disappears the
 * moment a persisted message carries its `clientMessageId`, and the live
 * assistant message disappears the moment the run's persisted assistant message
 * arrives. That ordering matters because SSE payload strings are truncated
 * display hints, so the streamed text must never outlive the real answer.
 *
 * A terminal run that persisted NO assistant message (a `funds_exhausted` turn
 * that produced no text) keeps its live message, which is the only place the
 * member is told why nothing came back.
 */
export function buildSpecThreadMessages(input: SpecThreadInput): ThreadMessageLike[] {
  const runsById = new Map((input.runs ?? []).map((record) => [record.id, record]));
  const sourceById = new Map(input.messages.map((message) => [message.id, message]));
  const out = specMessagesToThreadMessages(input.messages).map((entry) =>
    decorateWithRun(
      entry,
      entry.id ? sourceById.get(entry.id) : undefined,
      runsById,
      input.runDurations,
    ),
  );

  const { pending, stream } = input;
  if (pending) {
    const persisted = input.messages.some(
      (message) => message.clientMessageId === pending.clientMessageId,
    );
    if (!persisted) {
      out.push({
        id: `spec-pending:${pending.clientMessageId}`,
        role: 'user',
        content: asContent(
          pending.parts.flatMap((part) =>
            part.type === 'text' ? [{ type: 'text' as const, text: part.text }] : [],
          ),
        ),
        createdAt: new Date(pending.createdAt),
        metadata: {
          custom: {
            clientMessageId: pending.clientMessageId,
            optimistic: true,
            specFiles: specFilesOf(pending.parts),
          },
        },
      });
    }
  }

  if (stream) {
    /**
     * The overlay renders exactly what canonical history does not have yet.
     *
     * A run parked at `awaiting_approval` is not terminal, so no `run_ended`
     * marker closes the stream and nothing forces a history refetch. That
     * splits clients into two states that need DIFFERENT renderings of the same
     * stream, which is why a single boolean got this wrong:
     *
     *   - Never refetched: history holds no assistant message for the run, so
     *     the overlay is the only copy of the proposal card and must render the
     *     WHOLE accumulator. This is the card the deciding member clicks.
     *   - Refetched at park: history holds the canonical card, so the overlay
     *     must render ONLY what streamed after the park, or the card and its
     *     lead-in appear twice.
     *
     * Counting persisted assistant messages against `stream.parks` separates
     * them without a diff: history is behind while the count is at or below the
     * number of parks, and has caught up once it goes past. The final append at
     * settlement is what pushes it past, which is what retires the overlay.
     *
     * Persist coalesce (adjacent same-`runId` assistant rows in
     * `specMessagesToThreadMessages`) does not fold this live bubble. Until
     * history has the continuation, the overlay stays a second turn. Merging
     * it here would last-wins the synthetic `spec-run:` id, and Reload would
     * retry a message that is not in the transcript.
     */
    const persistedAssistantCount = input.messages.reduce(
      (count, message) =>
        message.role === 'assistant' && message.runId === stream.runId ? count + 1 : count,
      0,
    );
    const showsContinuationOnly = persistedAssistantCount > 0 && stream.parks > 0;
    if (persistedAssistantCount <= stream.parks) {
      const parts = showsContinuationOnly ? stream.parts.slice(stream.parkedParts) : stream.parts;
      const content: MutablePart[] = [];
      // First, because what was lost came BEFORE what survived. A partial
      // answer has to say so; it is replaced by the canonical message the
      // moment settlement persists one.
      if (stream.lostOutput) content.push({ type: 'text', text: SPEC_STREAM_GAP_NOTICE });
      // Arrival order, which is also the order the Worker persists, so the
      // settled message regroups nothing when it replaces this overlay.
      const lastPart = stream.parts.at(-1);
      for (const part of parts) {
        if (part.type === 'reasoning') {
          content.push({
            type: 'reasoning',
            text: part.text,
            status: { type: part === lastPart && !turnFinished(stream) ? 'running' : 'complete' },
          });
          continue;
        }
        if (part.type !== 'tool-call') {
          content.push({ type: 'text', text: part.text });
          continue;
        }
        const call = stream.toolCalls[part.index];
        if (!call) continue;
        // A frame arrives after the read settles, so an ordinary call carries
        // the same step result history will persist. A proposal keeps its own,
        // and a truncated one stays a plain card until settle supplies it.
        const result =
          call.result !== undefined || call.proposalTruncated
            ? call.result
            : {
                type: 'spec-tool-step',
                status: call.status,
                durationMs: call.durationMs ?? 0,
                resultBytes: call.resultBytes ?? 0,
                ...(call.summary ? { summary: call.summary } : {}),
                ...(call.projection ? { projection: call.projection } : {}),
                ...(call.projectsRead ? { projectsRead: call.projectsRead } : {}),
                ...(call.photoProjectId ? { photoProjectId: call.photoProjectId } : {}),
                ...(call.queryLimit ? { queryLimit: call.queryLimit } : {}),
              };
        content.push({
          type: 'tool-call',
          toolCallId: call.id,
          toolName: call.tool,
          ...(result !== undefined ? { result } : {}),
          ...(call.isError ? { isError: true } : {}),
        });
      }
      if (stream.degraded) content.push({ type: 'text', text: SPEC_STREAM_DEGRADED_NOTICE });
      // Nothing has streamed since the park, so there is no continuation to
      // show. Emitting anyway would put an empty bubble under the card the
      // member is being asked to decide, suggesting Spec is still working when
      // it is waiting on them.
      //
      // Tested on the CONTINUATION, not on `content`, because the gap and
      // degraded notices are pushed above it and would otherwise hold the
      // overlay open by themselves. That is not hypothetical on a parked run:
      // neither settle path is guaranteed to append a second assistant message
      // (deny writes a `tool` message and finalizes with empty text; approve
      // does the same when the continuation produces no prose), so the count
      // stays at one, this branch stays live, and a lone notice becomes a
      // permanent footnote under a settled card. The notices still ride along
      // whenever there IS continuation output to qualify.
      const isEmptyContinuation = showsContinuationOnly && parts.length === 0;
      // Running reads trail everything settled: the Worker persists each one where it finishes.
      const running: MutablePart[] =
        turnFinished(stream) || stream.cancelRequested
          ? []
          : stream.startedTools.map((started) => ({
              type: 'tool-call' as const,
              toolCallId: started.id,
              toolName: started.tool,
            }));
      if (!isEmptyContinuation) {
        out.push({
          id: `spec-run:${stream.runId}`,
          role: 'assistant',
          content: asContent([
            ...placeSearchRecords(content, drawnRecords(out), !turnFinished(stream)),
            ...running,
          ]),
          status: streamStatus(stream),
          metadata: {
            custom: {
              runId: stream.runId,
              streaming: !turnFinished(stream),
              lostOutput: stream.lostOutput,
              specLiveCites: stream.cites,
            },
            timing: streamTiming(stream),
          },
        });
      }
    }
  }

  // Spec's reply from the moment of send, not once the run is accepted, so the clock never restarts.
  if (input.sending && pending) {
    const persisted = input.messages.some(
      (message) => message.clientMessageId === pending.clientMessageId,
    );
    if (!persisted) {
      out.push({
        id: `spec-reply:${pending.clientMessageId}`,
        role: 'assistant',
        content: asContent([]),
        status: { type: 'running' },
        metadata: {
          custom: { streaming: true },
          timing: {
            streamStartTime: input.sentAt ?? Date.parse(pending.createdAt),
            totalChunks: 0,
            toolCallCount: 0,
          },
        },
      });
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface SpecListConversationsOptions {
  limit?: number;
  /**
   * Keyset cursor. BOTH halves are required together: without the id, tied
   * `lastMessageAt` timestamps are skipped across a page boundary, so a
   * half-supplied cursor is dropped rather than sent.
   */
  before?: string;
  beforeId?: string;
}

export interface StartSpecRunInput {
  workspaceId: string;
  conversationId: string;
  /** Idempotency key. Fresh per distinct send, reused verbatim on retry. */
  clientMessageId: string;
  parts: readonly SpecOutgoingPart[];
  context?: SpecContextEnvelope;
  skills?: readonly string[];
  connectionsOff?: readonly string[];
  webOff?: boolean;
  modelClass?: 'smart' | 'fast';
}

export interface SpecStartRunResult {
  runId: string;
  streamUrl: string;
  thread: SpecConversationSummary;
  run: SpecRunSummary;
  message: SpecMessage;
  /** True when this exact {conversation, clientMessageId} was already begun. */
  deduplicated: boolean;
  /** False when the run committed but its coordinator did not start. */
  coordinatorStarted: boolean;
}

export interface SpecStreamRunInput {
  workspaceId: string;
  runId: string;
  /** Reconnect cursor. `?after=<sequence>`, never `Last-Event-ID`. */
  after?: number;
  signal?: AbortSignal;
}

/**
 * The three answers a reconnect can get, plus an unavailable Worker.
 *
 * `gap` and `expired` are NOT errors: the Durable Object keeps a bounded replay
 * buffer, and either answer means the caller must reload canonical history from
 * the API rather than stitch a partial stream.
 */
export type SpecStreamConnection =
  | { kind: 'stream'; frames: AsyncIterable<SpecStreamFrame> }
  | { kind: 'gap'; oldestSequence: number | null }
  | { kind: 'expired' }
  | { kind: 'unavailable'; status: number; code: string };

/** Approval row statuses mirrored from `spec.approvals`. */
export type SpecApprovalStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'executing'
  | 'succeeded'
  | 'failed';

const SPEC_APPROVAL_STATUSES = new Set<string>([
  'pending',
  'approved',
  'denied',
  'expired',
  'executing',
  'succeeded',
  'failed',
]);

/** Member-facing approval card payload from GET / POST decision. */
export interface SpecApprovalCard {
  id: string;
  workspaceId: string;
  conversationId: string;
  runId: string;
  proposalStepId: string;
  continuationStepId: string | null;
  status: SpecApprovalStatus;
  toolName: string;
  toolVersion: string;
  requestHash: string;
  targetPreconditionHash: string;
  preview: unknown;
  decisionAt: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export type SpecApprovalDecision = 'approve' | 'deny';

export interface SpecDecideApprovalInput {
  workspaceId: string;
  approvalId: string;
  decision: SpecApprovalDecision;
  expectedRequestHash: string;
}

/** Statuses that still need polling after a decision or on reload. */
export function isSpecApprovalTerminal(status: SpecApprovalStatus): boolean {
  return (
    status === 'denied' || status === 'expired' || status === 'succeeded' || status === 'failed'
  );
}

function unreadableApproval(): SpecClientError {
  return new SpecClientError(502, 'spec_approval_unreadable', 'Spec could not read this approval.');
}

function readSpecApprovalCard(raw: unknown): SpecApprovalCard {
  const record = partRecord(raw);
  if (!record) throw unreadableApproval();
  const id = readString(record.id);
  const workspaceId = readString(record.workspaceId);
  const conversationId = readString(record.conversationId);
  const runId = readString(record.runId);
  const proposalStepId = readString(record.proposalStepId);
  const statusRaw = readString(record.status);
  const toolName = readString(record.toolName);
  const toolVersion = readString(record.toolVersion);
  const requestHash = readString(record.requestHash);
  const targetPreconditionHash = readString(record.targetPreconditionHash);
  const expiresAt = readString(record.expiresAt);
  const createdAt = readString(record.createdAt);
  const updatedAt = readString(record.updatedAt);
  if (
    !id ||
    !workspaceId ||
    !conversationId ||
    !runId ||
    !proposalStepId ||
    !statusRaw ||
    !SPEC_APPROVAL_STATUSES.has(statusRaw) ||
    !toolName ||
    !toolVersion ||
    !requestHash ||
    !targetPreconditionHash ||
    !expiresAt ||
    !createdAt ||
    !updatedAt
  ) {
    throw unreadableApproval();
  }
  return {
    id,
    workspaceId,
    conversationId,
    runId,
    proposalStepId,
    continuationStepId: readString(record.continuationStepId),
    status: statusRaw as SpecApprovalStatus,
    toolName,
    toolVersion,
    requestHash,
    targetPreconditionHash,
    preview: record.preview,
    decisionAt: readString(record.decisionAt),
    expiresAt,
    createdAt,
    updatedAt,
  };
}

export interface SpecWebClient {
  bootstrap(workspaceId: string): Promise<SpecBootstrap>;
  /**
   * The workspace's live Spec balance, from the narrow read built for it.
   *
   * Deliberately NOT a second bootstrap. Bootstrap is the Spec Worker's
   * per-request gate: it resolves membership, launch state, capabilities and
   * five ledger tables, and the API has already had to move work off that path
   * once. This endpoint is the same shared `readSpecMemberUsage` and nothing
   * else, so refreshing the number chat guards on costs one read.
   */
  getUsage(workspaceId: string): Promise<SpecUsageSummary>;
  listConversations(
    workspaceId: string,
    options?: SpecListConversationsOptions,
  ): Promise<readonly SpecConversationSummary[]>;
  getConversation(
    workspaceId: string,
    conversationId: string,
    options?: { messageLimit?: number },
  ): Promise<SpecConversationDetail>;
  createConversation(
    workspaceId: string,
    title?: string | null,
    options?: SpecCreateConversationOptions,
  ): Promise<SpecCreatedConversation>;
  renameConversation(
    workspaceId: string,
    conversationId: string,
    title: string,
  ): Promise<SpecConversationSummary>;
  markConversationSeen(
    workspaceId: string,
    conversationId: string,
  ): Promise<{ id: string; seenAnswerAt: string }>;
  deleteConversation(workspaceId: string, conversationId: string): Promise<void>;
  deleteAllConversations(workspaceId: string): Promise<void>;
  getSpecPrivacy(workspaceId: string): Promise<{ retentionDays: number | null }>;
  setSpecRetention(
    workspaceId: string,
    retentionDays: 30 | 90 | 365 | null,
  ): Promise<{ retentionDays: number | null }>;
  purgeSpecMember(
    workspaceId: string,
    memberId: string,
  ): Promise<{ job: { id: string; kind: string; targetMemberId: string | null } }>;
  getContextSnapshot(
    workspaceId: string,
    conversationId: string,
    snapshotId: string,
  ): Promise<SpecContextSnapshot>;
  /** What one tool step sent and received; null when there is no trace. Optional so a fake client need not carry it. */
  getToolTrace?(
    workspaceId: string,
    conversationId: string,
    toolCallId: string,
  ): Promise<SpecToolTrace | null>;
  /** Rate an answer for the calling member; a null rating clears it. Optional like getToolTrace. */
  setMessageFeedback?(
    workspaceId: string,
    conversationId: string,
    messageId: string,
    body: { rating: SpecFeedbackRating | null; reason?: SpecFeedbackReason; note?: string },
  ): Promise<void>;
  /** Edit-truncate: the latest user message and everything after it leave the transcript. */
  supersedeMessage?(
    workspaceId: string,
    conversationId: string,
    messageId: string,
  ): Promise<{ superseded: string[] }>;
  /** Chat files: optional like getToolTrace, so a fake client need not carry them. */
  uploadFile?(workspaceId: string, file: File): Promise<SpecFileRecord>;
  getFile?(workspaceId: string, fileId: string): Promise<SpecFileRecord>;
  deleteFile?(workspaceId: string, fileId: string): Promise<void>;
  fileContent?(workspaceId: string, fileId: string): Promise<Blob>;
  /** Actor-only approval card for reload / another-device refresh. Parcel API. */
  getApproval(workspaceId: string, approvalId: string): Promise<SpecApprovalCard>;
  /**
   * Approve once or deny via the Spec Worker (which binds continuation and
   * wakes the coordinator). Body carries the preview request hash.
   */
  decideApproval(input: SpecDecideApprovalInput): Promise<SpecApprovalCard>;
  startRun(input: StartSpecRunInput): Promise<SpecStartRunResult>;
  streamRun(input: SpecStreamRunInput): Promise<SpecStreamConnection>;
  cancelRun(workspaceId: string, runId: string): Promise<void>;
}

export interface SpecToolTrace {
  toolName: string;
  arguments: unknown;
  result: unknown;
  resultTruncated: boolean;
  status: 'ok' | 'error' | 'refused';
}

/** Narrow by hand, like the other readers here: the body is untrusted until it has this shape. */
export function readSpecToolTrace(value: unknown): SpecToolTrace | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.toolName !== 'string') return null;
  const status = record.status;
  if (status !== 'ok' && status !== 'error' && status !== 'refused') return null;
  return {
    toolName: record.toolName,
    arguments: record.arguments ?? null,
    result: record.result ?? null,
    resultTruncated: record.resultTruncated === true,
    status,
  };
}

export interface SpecWebClientOptions {
  /** Spec Worker origin. Defaults to `VITE_SPEC_URL`; null disables the Worker. */
  baseUrl?: string | null;
  fetchImpl?: typeof fetch;
  getAccessToken?: () => Promise<string | null>;
  apiFetchImpl?: <T>(path: string, init?: RequestInit) => Promise<T>;
  apiFetchBlobImpl?: (path: string, init?: RequestInit) => Promise<Blob>;
}

// Re-exported so this module stays the one import for Spec callers, while
// `__root.tsx` can read the gate from `spec-config.js` alone and keep the chat
// runtime out of the entry chunk.
export { isSpecWorkerConfigured, readSpecWorkerBaseUrl };

/** Fresh idempotency key. One per distinct send; a retry reuses the old one. */
export function newSpecClientMessageId(): string {
  const source = globalThis.crypto;
  if (source && typeof source.randomUUID === 'function') return source.randomUUID();
  return `spec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const LAUNCH_STATES = new Set<string>(['off', 'read_only', 'on']);
const MEMBER_SEAT_STATES = new Set<string>(['not_required', 'assigned', 'required']);

function unreadableBootstrap(): SpecClientError {
  return new SpecClientError(
    502,
    'spec_bootstrap_unreadable',
    'Spec could not confirm whether it is available.',
  );
}

/**
 * Narrow a bootstrap response, failing closed.
 *
 * Only the fields that GATE the surface are enforced: an unreadable launch
 * state, workspace, member, or `useSpec` capability raises rather than
 * defaulting to something permissive. The usage numbers are passed through,
 * because a display-only shape change must not be able to make chat look
 * unavailable.
 */
function readSpecBootstrap(raw: unknown): SpecBootstrap {
  if (typeof raw !== 'object' || raw === null) throw unreadableBootstrap();
  const record = raw as Record<string, unknown>;
  const launchState = record.launchState;
  if (typeof launchState !== 'string' || !LAUNCH_STATES.has(launchState)) {
    throw unreadableBootstrap();
  }
  const workspaceId = readString(record.workspaceId);
  const memberId = readString(record.memberId);
  if (!workspaceId || !memberId) throw unreadableBootstrap();
  const capabilities = partRecord(record.capabilities);
  if (!capabilities || typeof capabilities.useSpec !== 'boolean') throw unreadableBootstrap();

  return {
    launchState: launchState as SpecLaunchState,
    workspaceId,
    memberId,
    capabilities: {
      useSpec: capabilities.useSpec,
      manageTools: capabilities.manageTools === true,
      manageConnections: capabilities.manageConnections === true,
      manageSkills: capabilities.manageSkills === true,
      manageBilling: capabilities.manageBilling === true,
    },
    // Pinned by the shared schema, not chosen here. There is no model picker:
    // `Spec 1.0` is the label for a normal turn.
    modelLabels: { smart: 'Spec 1.0', fast: 'Spec 1.0 fast' },
    usage: record.usage as SpecUsageSummary,
    // Defaults PERMISSIVE, unlike the gating fields above. An API that does not
    // report a seat state is one that does not bill this workspace by seat as
    // far as this client can tell, and reading an absent value as `required`
    // would block every member of every Free and trial workspace. The server
    // still refuses an unassigned member, so the cost of being wrong here is a
    // clear refusal rather than a product-wide lockout.
    memberSeat: MEMBER_SEAT_STATES.has(record.memberSeat as string)
      ? (record.memberSeat as SpecMemberSeatState)
      : 'not_required',
  };
}

/** Keep history readable when a binding is absent or unreadable. */
function readConversationDetail(raw: SpecConversationDetail): SpecConversationDetail {
  const sessionRaw = (raw as { skillAuthoringSession?: unknown }).skillAuthoringSession;
  if (sessionRaw === undefined) return raw;
  const parsed = SkillAuthoringSessionSummarySchema.safeParse(sessionRaw);
  if (!parsed.success) {
    const { skillAuthoringSession: _dropped, ...rest } = raw;
    return rest;
  }
  return { ...raw, skillAuthoringSession: parsed.data };
}

/**
 * The `usage` block out of `{workspaceId, memberId, usage}`.
 *
 * Fails CLOSED on an unreadable envelope, and that means throwing rather than
 * returning a fabricated balance: the caller keeps the last balance the server
 * gave it. The alternative -- passing an absent `usage` through -- would
 * overwrite a good number with `undefined`, and the guard reads an unreadable
 * summary as "no capacity is known", which is not the same as "no capacity".
 *
 * The numbers themselves are NOT validated here, exactly as in
 * `readSpecBootstrap`: a display-only shape change must not be able to make
 * chat look unavailable. `specUsageGuardFor` is where the shape is checked, in
 * one place, for both the bootstrap copy and this one.
 */
function readSpecUsageEnvelope(raw: unknown): SpecUsageSummary {
  const record = partRecord(raw);
  const usage = record ? partRecord(record.usage) : null;
  if (!usage) {
    throw new SpecClientError(
      502,
      'spec_usage_unreadable',
      'Spec could not read this workspace balance.',
    );
  }
  return usage as unknown as SpecUsageSummary;
}

function requireSpecFileRecord(value: unknown): SpecFileRecord {
  const record = readSpecFileRecord(value);
  if (!record)
    throw new SpecClientError(502, 'spec_file_unreadable', 'Spec could not read this file.');
  return record;
}

async function readWorkerCode(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as unknown;
  const record = partRecord(body);
  if (record) {
    const direct = readString(record.code);
    if (direct) return direct;
    const nested = partRecord(record.error);
    const nestedCode = nested ? readString(nested.code) : null;
    if (nestedCode) return nestedCode;
  }
  return `http_${response.status}`;
}

export function createSpecWebClient(options: SpecWebClientOptions = {}): SpecWebClient {
  const baseUrl =
    options.baseUrl === undefined ? SPEC_WORKER_BASE_URL : readSpecWorkerBaseUrl(options.baseUrl);
  const doApiFetch =
    options.apiFetchImpl ??
    (<T>(path: string, init?: RequestInit) => apiFetch<T>(path, init ?? {}));
  const doApiFetchBlob = options.apiFetchBlobImpl ?? apiFetchBlob;
  const doFetch: typeof fetch = options.fetchImpl ?? ((...args) => fetch(...args));
  const getAccessToken =
    options.getAccessToken ??
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    });

  function workerUrl(
    path: string,
    workspaceId: string,
    query: Record<string, string> = {},
  ): string {
    if (!baseUrl) {
      throw new SpecClientError(
        503,
        'spec_not_configured',
        'Spec is not available in this environment.',
      );
    }
    const url = new URL(`${baseUrl}${path}`);
    url.searchParams.set('workspace_id', workspaceId);
    for (const [key, value] of Object.entries(query)) url.searchParams.set(key, value);
    return url.toString();
  }

  async function authorized(init: RequestInit): Promise<RequestInit> {
    const token = await getAccessToken();
    if (!token) {
      throw new SpecClientError(401, 'spec_unauthenticated', 'Sign in to use Spec.');
    }
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return { ...init, headers };
  }

  async function workerFailure(response: Response): Promise<SpecClientError> {
    return new SpecClientError(response.status, await readWorkerCode(response));
  }

  return {
    async bootstrap(workspaceId) {
      return readSpecBootstrap(
        await doApiFetch<unknown>(`/workspaces/${workspaceId}/spec/bootstrap`),
      );
    },

    async getUsage(workspaceId) {
      return readSpecUsageEnvelope(
        await doApiFetch<unknown>(`/workspaces/${workspaceId}/spec/usage`),
      );
    },

    async listConversations(workspaceId, listOptions = {}) {
      const params = new URLSearchParams();
      if (listOptions.limit != null) params.set('limit', String(listOptions.limit));
      if (listOptions.before && listOptions.beforeId) {
        params.set('before', listOptions.before);
        params.set('before_id', listOptions.beforeId);
      }
      const query = params.toString();
      const body = await doApiFetch<{ conversations: SpecConversationSummary[] }>(
        `/workspaces/${workspaceId}/spec/conversations${query ? `?${query}` : ''}`,
      );
      return body.conversations ?? [];
    },

    async getConversation(workspaceId, conversationId, detailOptions = {}) {
      const query =
        detailOptions.messageLimit == null
          ? ''
          : `?message_limit=${encodeURIComponent(String(detailOptions.messageLimit))}`;
      const body = await doApiFetch<SpecConversationDetail>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}${query}`,
      );
      return readConversationDetail(body);
    },

    async createConversation(workspaceId, title = null, createOptions = {}) {
      const workflow = createOptions.skillWorkflow;
      const body = await doApiFetch<{
        conversation: SpecConversationSummary;
        skillAuthoringSession?: unknown;
      }>(`/workspaces/${workspaceId}/spec/conversations`, {
        method: 'POST',
        body: JSON.stringify(workflow ? { title, skillWorkflow: workflow } : { title }),
      });
      if (!workflow) return body.conversation;
      const session = SkillAuthoringSessionSummarySchema.safeParse(body.skillAuthoringSession);
      // Fail closed: an authoring UI must never treat an unbound conversation as bound.
      if (!session.success) {
        throw new SpecClientError(
          502,
          'spec_authoring_session_unreadable',
          'The server did not return the skill authoring session for this conversation.',
        );
      }
      return { ...body.conversation, skillAuthoringSession: session.data };
    },

    async renameConversation(workspaceId, conversationId, title) {
      const body = await doApiFetch<{ conversation: SpecConversationSummary }>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}`,
        { method: 'PATCH', body: JSON.stringify({ title }) },
      );
      return body.conversation;
    },

    async markConversationSeen(workspaceId, conversationId) {
      const body = await doApiFetch<{ conversation: { id: string; seenAnswerAt: string } }>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}/seen`,
        { method: 'POST' },
      );
      return body.conversation;
    },

    async deleteConversation(workspaceId, conversationId) {
      await doApiFetch<unknown>(`/workspaces/${workspaceId}/spec/conversations/${conversationId}`, {
        method: 'DELETE',
      });
    },

    async deleteAllConversations(workspaceId) {
      await doApiFetch<unknown>(`/workspaces/${workspaceId}/spec/conversations/delete-all`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async getSpecPrivacy(workspaceId) {
      return doApiFetch<{ retentionDays: number | null }>(
        `/workspaces/${workspaceId}/spec/privacy`,
      );
    },

    async setSpecRetention(workspaceId, retentionDays) {
      return doApiFetch<{ retentionDays: number | null }>(
        `/workspaces/${workspaceId}/spec/privacy/retention`,
        { method: 'PATCH', body: JSON.stringify({ retentionDays }) },
      );
    },

    async purgeSpecMember(workspaceId, memberId) {
      return doApiFetch<{ job: { id: string; kind: string; targetMemberId: string | null } }>(
        `/workspaces/${workspaceId}/spec/privacy/members/${memberId}/purge`,
        { method: 'POST', body: JSON.stringify({}) },
      );
    },

    async getContextSnapshot(workspaceId, conversationId, snapshotId) {
      const body = await doApiFetch<{ contextSnapshot: unknown }>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}/context-snapshots/${snapshotId}`,
      );
      return readSpecContextSnapshot(body.contextSnapshot);
    },

    async getToolTrace(workspaceId, conversationId, toolCallId) {
      try {
        const body = await doApiFetch<{ toolTrace: unknown }>(
          `/workspaces/${workspaceId}/spec/conversations/${conversationId}/tool-traces/${encodeURIComponent(toolCallId)}`,
        );
        return readSpecToolTrace(body.toolTrace);
      } catch (error) {
        // Written before traces shipped, expired with its run, or never recorded: all "no detail".
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },

    async setMessageFeedback(workspaceId, conversationId, messageId, body) {
      await doApiFetch<unknown>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}/messages/${messageId}/feedback`,
        { method: 'PUT', body: JSON.stringify(body) },
      );
    },

    async uploadFile(workspaceId, file) {
      const form = new FormData();
      form.set('file', file);
      const body = await doApiFetch<{ file: unknown }>(`/workspaces/${workspaceId}/spec/files`, {
        method: 'POST',
        body: form,
      });
      return requireSpecFileRecord(body.file);
    },

    async getFile(workspaceId, fileId) {
      const body = await doApiFetch<{ file: unknown }>(
        `/workspaces/${workspaceId}/spec/files/${encodeURIComponent(fileId)}`,
      );
      return requireSpecFileRecord(body.file);
    },

    async deleteFile(workspaceId, fileId) {
      await doApiFetch<unknown>(
        `/workspaces/${workspaceId}/spec/files/${encodeURIComponent(fileId)}`,
        { method: 'DELETE' },
      );
    },

    async fileContent(workspaceId, fileId) {
      return doApiFetchBlob(
        `/workspaces/${workspaceId}/spec/files/${encodeURIComponent(fileId)}/content`,
      );
    },

    async supersedeMessage(workspaceId, conversationId, messageId) {
      return doApiFetch<{ superseded: string[] }>(
        `/workspaces/${workspaceId}/spec/conversations/${conversationId}/messages/${messageId}/supersede`,
        { method: 'POST', body: JSON.stringify({}) },
      );
    },

    async getApproval(workspaceId, approvalId) {
      const body = await doApiFetch<{ approval: unknown }>(
        `/workspaces/${workspaceId}/spec/approvals/${approvalId}`,
      );
      return readSpecApprovalCard(body.approval);
    },

    async decideApproval(input) {
      const url = workerUrl(
        `/v1/approvals/${encodeURIComponent(input.approvalId)}/decision`,
        input.workspaceId,
      );
      const response = await doFetch(
        url,
        await authorized({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            decision: input.decision,
            expectedRequestHash: input.expectedRequestHash,
          }),
        }),
      );
      if (!response.ok) throw await workerFailure(response);
      const payload = partRecord(await response.json().catch(() => null));
      if (!payload) throw unreadableApproval();
      return readSpecApprovalCard(payload.approval);
    },

    async startRun(input) {
      const url = workerUrl(
        `/v1/threads/${encodeURIComponent(input.conversationId)}/messages`,
        input.workspaceId,
      );
      const body: Record<string, unknown> = {
        clientMessageId: input.clientMessageId,
        // Copied key by key, so nothing a caller adds to a part reaches the wire.
        parts: input.parts.map((part) =>
          part.type === 'text'
            ? { type: 'text', text: part.text }
            : {
                type: 'file',
                fileId: part.fileId,
                name: part.name,
                mimeType: part.mimeType,
                size: part.size,
                kind: part.kind,
                ...(part.pages === undefined ? {} : { pages: part.pages }),
              },
        ),
      };
      if (input.context) body.context = toSpecContextEnvelopeBody(input.context);
      if (input.skills?.length) body.skills = [...input.skills];
      if (input.connectionsOff?.length) body.connectionsOff = [...input.connectionsOff];
      if (input.webOff) body.webOff = true;
      if (input.modelClass) body.modelClass = input.modelClass;

      const response = await doFetch(
        url,
        await authorized({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      );
      if (!response.ok) throw await workerFailure(response);

      const payload = partRecord(await response.json().catch(() => null));
      const run = payload ? partRecord(payload.run) : null;
      const runId = run ? readString(run.id) : null;
      if (!payload || !runId) {
        // Fail closed rather than invent a run id: a stream or cancel aimed at
        // the wrong run is worse than a visible send failure.
        throw new SpecClientError(
          502,
          'spec_start_unreadable',
          'Spec accepted the message but did not name the run.',
        );
      }
      return {
        runId,
        streamUrl: readString(payload.streamUrl) ?? '',
        thread: payload.thread as SpecConversationSummary,
        run: payload.run as SpecRunSummary,
        message: payload.message as SpecMessage,
        deduplicated: payload.deduplicated === true,
        coordinatorStarted: payload.coordinatorStarted !== false,
      };
    },

    async streamRun(input) {
      const query: Record<string, string> = {};
      const after = input.after ?? 0;
      if (Number.isSafeInteger(after) && after > 0) query.after = String(after);
      const url = workerUrl(
        `/v1/runs/${encodeURIComponent(input.runId)}/events`,
        input.workspaceId,
        query,
      );
      const response = await doFetch(
        url,
        await authorized({
          method: 'GET',
          headers: { Accept: 'text/event-stream' },
          ...(input.signal ? { signal: input.signal } : {}),
        }),
      );

      if (response.status === 410) {
        const record = partRecord(await response.json().catch(() => null));
        const code = record ? readString(record.code) : null;
        if (code === 'spec_replay_expired') return { kind: 'expired' };
        const oldest = record ? readCount(record.oldestSequence) : null;
        return { kind: 'gap', oldestSequence: oldest };
      }
      if (!response.ok) {
        return {
          kind: 'unavailable',
          status: response.status,
          code: await readWorkerCode(response),
        };
      }
      if (!response.body) {
        return { kind: 'unavailable', status: response.status, code: 'spec_stream_unreadable' };
      }
      return { kind: 'stream', frames: readSpecSseStream(response.body) };
    },

    async cancelRun(workspaceId, runId) {
      const url = workerUrl(`/v1/runs/${encodeURIComponent(runId)}/cancel`, workspaceId);
      const response = await doFetch(url, await authorized({ method: 'POST' }));
      if (!response.ok) throw await workerFailure(response);
    },
  };
}

/** Default client, wired to the app's real API helper and Supabase session. */
export const specWebClient: SpecWebClient = createSpecWebClient();
